/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Avatar, Box, Button, Text } from 'grommet';
import axios from '../../helpers/axios';
import { withFirebase } from '../../services/Firebase';

import * as Sentry from '@sentry/browser';
import ApiError from '../../helpers/ApiError';
import { dtos } from '@shd/jslib/models';
import {
  Destination,
  PAGE_TYPE,
  PROFILE_TYPE,
  Page,
  PageResponse,
  Paging,
  Profile,
  callFbApi,
  getFacebookAuthResponse,
} from '../../helpers/facebook';
import { errorUtil, strogging } from '@shd/jslib/infra';
import { analytics } from '../../services/analytics';
import SHDText from '@ui/Text/SHDText';
import SHDCallout from '@ui/Callout/SHDCallout';
import { colors } from '@material-ui/core';
import SHDButton from '@ui/Button/SHDButton';

interface CardProps {
  onClick: () => void;
  disabled: boolean | undefined;
  pictureUrl: string;
  name: string;
  subtitle: string;
}

export const CustomCard: React.FC<CardProps> = ({
  onClick,
  disabled,
  pictureUrl,
  name,
  subtitle,
}) => {
  return (
    <SHDCallout round="medium">
      <Button onClick={onClick} disabled={disabled}>
        <Box
          direction="row"
          gap="large"
          align="center"
          style={{ marginTop: 10, marginBottom: 10 }}
        >
          <Box flex={{ shrink: 0, grow: 0 }}>
            {pictureUrl ? (
              <Avatar src={pictureUrl} />
            ) : (
              <Avatar background="brand">
                {name ? name.substring(0, 2) : 'XY'}
              </Avatar>
            )}
          </Box>
          <Box>
            <SHDText style="subhead-bold">{name}</SHDText>
            <SHDText style="body" color={colors.grey[500]}>
              {subtitle}
            </SHDText>
          </Box>
        </Box>
      </Button>
    </SHDCallout>
  );
};

interface ProfilePickerProps {
  onSave: (destination: Destination) => void;
}
const ProfilePicker: React.FC<ProfilePickerProps> = ({ onSave }) => {
  const [profile, setProfile] = useState<Profile>();
  const [error, setError] = useState<string | null>();
  const getProfile = () =>
    callFbApi<Profile>('/me?fields=name,email,picture,link');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getProfile();
        setProfile(response);
      } catch (err) {
        setError('Failed to fetch profile. Please try again.');
      }
    };

    fetchProfile();
  }, []);

  return (
    <Box gap="medium">
      {profile && (
        <CustomCard
          onClick={() => {
            onSave({
              type: PROFILE_TYPE,
              ...profile,
            });
          }}
          name={profile?.name}
          subtitle="Personal profile"
          disabled={profile.disabled}
          pictureUrl={profile?.picture.data.url}
        />
      )}
      {error && <Text color="status-error">{error}</Text>}
    </Box>
  );
};

interface PagesPickerProps {
  onSave: (destination: Destination) => void;
}
const PagesPicker: React.FC<PagesPickerProps> = ({ onSave }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>();
  const [pages, setPages] = useState<Page[]>([]);
  const [pagesPagination, setPagesPagination] = useState<Paging | null>(null);

  const getPages = () =>
    callFbApi<PageResponse>('/me/accounts?fields=id,name,picture,tasks');

  useEffect(() => {
    const fetchPages = async () => {
      setLoading(true);
      try {
        const pagesResponse = await getPages();
        setPages(
          pagesResponse.data.map((page) => ({
            ...page,
            disabled: page.tasks && !page.tasks.includes('CREATE_CONTENT'),
          }))
        );
        setPagesPagination(pagesResponse.paging);
      } catch (err) {
        strogging.error('fetchPages-exception', errorUtil.errorPayload(err));
        setError('Failed to load pages. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, []);

  const morePagesClicked = () => {
    if (!pagesPagination) {
      return;
    }
    axios.get(pagesPagination.next).then((res) => {
      const newPages = res.data.map((page: Page) => ({
        ...page,
        label: page.name,
        value: page.id,
      }));
      setPages([...pages, ...newPages]);
      setPagesPagination(res.data.paging);
    });
  };

  return (
    <Box gap="large" skeleton={loading}>
      <Box gap="small">
        {pages.map((page, index) => (
          <CustomCard
            key={`option-${index + 1}`}
            onClick={() => {
              onSave({
                type: PAGE_TYPE,
                ...pages[index],
              });
            }}
            name={page?.name}
            subtitle="Page"
            disabled={page.disabled}
            pictureUrl={page?.picture.data.url}
          />
        ))}
        {error && <Text color="status-error">{error}</Text>}
      </Box>
      {pagesPagination?.next ? (
        <Button
          secondary
          pad="small"
          label="Load more"
          onClick={() => {
            morePagesClicked();
          }}
        />
      ) : null}
    </Box>
  );
};

interface DestinationLinkSuccessProps {
  onClose: () => void;
  destination: Destination;
  isLivestreamEligible: boolean;
  setStepDestinationTitle: (title: string) => void;
}
const DestinationLinkSuccess: React.FC<DestinationLinkSuccessProps> = ({
  onClose,
  destination,
  setStepDestinationTitle,
}) => {
  setStepDestinationTitle(
    `You can now stream to your ${destination.type === 'page' ? destination.name : 'Facebook profile'}`
  );
  return (
    <Box style={{ marginTop: -20 }} gap="large" fill="vertical">
      <SHDText style="footnote">{`Sideline HD will now stream to the following ${destination.name}`}</SHDText>
      <Box gap="large" flex="grow" overflow="auto">
        <CustomCard
          onClick={() => {}}
          name={destination?.name}
          subtitle=""
          disabled={false}
          pictureUrl={destination?.picture.data.url}
        />
      </Box>
      <SHDButton primary label="Done" onClick={onClose} />
    </Box>
  );
};

const PageLinkFailure: React.FC<{
  page: Destination;
  error: ApiError;
  setStepDestinationTitle: (title: string) => void;
  setDestinationGoBack: () => void;
}> = ({ page, error, setStepDestinationTitle, setDestinationGoBack }) => {
  setStepDestinationTitle(`A stream link will be posted to ${page.name}`);
  return (
    <Box gap="large" fill="vertical">
      <CustomCard
        onClick={() => {}}
        name={page?.name}
        subtitle=""
        disabled={false}
        pictureUrl={page?.picture.data.url}
      />
      <Box gap="small" flex="grow" overflow="auto">
        <SHDText style="body-semibold">
          Your page doesn’t have established presence.
        </SHDText>
        <SHDText style="body">
          Facebook requires your account to have an established presence before
          you can go live. We suggest continuing to grow your page and trying
          again after a while to see if its status has changed.
        </SHDText>
        <SHDText style="body">
          In the meantime, we will create a post on your page with a link to
          your stream on sidelineLive.
        </SHDText>
      </Box>
      {error && <Text color="status-error">{error.message}</Text>}
      <SHDButton primary label="Done" onClick={setDestinationGoBack} />
    </Box>
  );
};

interface Props {
  teamId: string;
  onClose: () => void;
  onLinked: (linkedChannel: dtos.PublishFb) => void;
  setStepDestinationTitle: (title: string) => void;
  setDestinationGoBack: (goBack: unknown) => void;
}

const FacebookDestinationPickerV3: React.FC<Props> = ({
  teamId,
  onClose,
  onLinked,
  setStepDestinationTitle,
  setDestinationGoBack,
}) => {
  const [error, setError] = useState<string | null>();
  const [linkError, setLinkError] = useState<ApiError>();

  const [destination, setDestination] = useState<Destination | null>(null);
  const [step, setStep] = useState(1); // consolidate into steps and use named constants for steps
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLivestreamEligible, setIsLivestreamEligible] = useState(false);

  const onSave = async (d: Destination) => {
    const authResponse = await getFacebookAuthResponse();
    if (authResponse) {
      axios
        .post('/api/facebook_auth', {
          authResponse,
          linkedChannel: d,
          teamId,
        })
        .then((response) => {
          analytics.track('Streaming Destination Linked', {
            destinationType: 'Facebook',
            destinationSubtype: d.type,
            teamId,
          });

          // TODO: implement some sort of warning for token expiration
          // onComplete();
          // updateLinkedChannel(selectedChannel);
          // const { accessTokenExpirationTs } = response.data;
          // const ts = Date.now();

          // if (ts / 1000 > accessTokenExpirationTs - expirationThreshold) {
          //   const now = DateTime.fromMillis(ts);
          //   const expiration = DateTime.fromSeconds(accessTokenExpirationTs);
          //   const daysUntilExpiration = Math.round(
          //     expiration.diff(now, ['days']).toObject().days
          //   );

          //   setShowExpiration({ daysUntilExpiration });
          // }
          // setLinkingError(null);
          // setLinkingLoading(false);
          setShowSuccess(true);
          setDestination(d);
          setDestinationGoBack(undefined);
          setStep(3);
          setLinkError(undefined);
          const publishFb = response.data.publishFb;
          onLinked(publishFb);
          setIsLivestreamEligible(response.data.isLivestreamEligible);
        })
        .catch((e) => {
          Sentry.captureException(e);
          const apiError = new ApiError(e);
          strogging.error('Failed to link Facebook', {
            message: apiError.message,
            details: apiError.details,
          });
          setDestination(d);
          setStep(3);
          setLinkError(apiError);
          setError(apiError.message);
        });
    }
  };

  const goToStep1 = () => {
    setStep(1); // Go back to the first step
    setStepDestinationTitle('Where do you want to stream to?');
  };

  useEffect(() => {
    if (step === 3) {
      setDestinationGoBack(() => () => {
        goToStep1();
      });
    }
    if (step === 1) {
      setDestinationGoBack(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, setDestinationGoBack, setStepDestinationTitle]);

  return (
    <>
      {step === 1 && (
        <Box gap="medium" fill="vertical">
          <ProfilePicker onSave={onSave} />
          <PagesPicker onSave={onSave} />
          <Box align="center">
            <SHDText style="footnote" textAlign="center">
              Don’t see your page? Make sure you are an Admin, not an Editor or
              Moderator.
            </SHDText>
          </Box>
        </Box>
      )}
      {showSuccess && destination && step === 3 && (
        <DestinationLinkSuccess
          destination={destination}
          onClose={onClose}
          isLivestreamEligible={isLivestreamEligible}
          setStepDestinationTitle={setStepDestinationTitle}
        />
      )}
      {linkError &&
        destination &&
        destination.type === PAGE_TYPE &&
        step === 3 && (
          <PageLinkFailure
            page={destination}
            error={linkError}
            setStepDestinationTitle={setStepDestinationTitle}
            setDestinationGoBack={goToStep1}
          />
        )}
      {error && <Text color="status-error">{JSON.stringify(error)}</Text>}
    </>
  );
};

export default observer(withFirebase(FacebookDestinationPickerV3));
